// 🔥 성능 최적화 유틸리티 - 최고급 구현
// [배경] Firebase 조회 성능 최적화, 메모리 관리, 네트워크 최적화

/**
 * 🔥 고급 메모이제이션 유틸리티
 */
export class AdvancedMemoization {
  private static cache = new Map<string, { data: any; timestamp: number; hits: number }>();
  private static readonly MAX_CACHE_SIZE = 1000;
  private static readonly DEFAULT_TTL = 10 * 60 * 1000; // 10분
  
  /**
   * 메모이제이션된 함수 생성
   */
  static memoize<T extends (...args: any[]) => any>(
    fn: T,
    options: {
      keyGenerator?: (...args: Parameters<T>) => string;
      ttl?: number;
      maxSize?: number;
    } = {}
  ): T {
    const { 
      keyGenerator = (...args) => JSON.stringify(args),
      ttl = this.DEFAULT_TTL,
      maxSize = this.MAX_CACHE_SIZE
    } = options;
    
    return ((...args: Parameters<T>) => {
      const key = keyGenerator(...args);
      const cached = this.cache.get(key);
      
      // 캐시 히트 + 유효성 검사
      if (cached && Date.now() - cached.timestamp < ttl) {
        cached.hits++;
        return cached.data;
      }
      
      // 캐시 크기 관리 (LRU 방식)
      if (this.cache.size >= maxSize) {
        this.evictLeastUsed();
      }
      
      // 함수 실행 및 캐시 저장
      const result = fn(...args);
      this.cache.set(key, {
        data: result,
        timestamp: Date.now(),
        hits: 1
      });
      
      return result;
    }) as T;
  }
  
  /**
   * LRU 방식으로 캐시 엔트리 제거
   */
  private static evictLeastUsed(): void {
    let leastUsedKey = '';
    let minHits = Infinity;
    let oldestTime = Infinity;
    
    for (const [key, value] of this.cache.entries()) {
      if (value.hits < minHits || (value.hits === minHits && value.timestamp < oldestTime)) {
        minHits = value.hits;
        oldestTime = value.timestamp;
        leastUsedKey = key;
      }
    }
    
    if (leastUsedKey) {
      this.cache.delete(leastUsedKey);
    }
  }
  
  /**
   * 캐시 통계 조회
   */
  static getCacheStats() {
    const entries = Array.from(this.cache.values());
    return {
      size: this.cache.size,
      totalHits: entries.reduce((sum, entry) => sum + entry.hits, 0),
      averageAge: entries.length > 0 
        ? (Date.now() - entries.reduce((sum, entry) => sum + entry.timestamp, 0) / entries.length) / 1000
        : 0
    };
  }
  
  /**
   * 캐시 수동 무효화
   */
  static invalidate(pattern?: string): void {
    if (!pattern) {
      this.cache.clear();
      return;
    }
    
    const keysToDelete = Array.from(this.cache.keys())
      .filter(key => key.includes(pattern));
    
    keysToDelete.forEach(key => this.cache.delete(key));
  }
}

/**
 * 🔥 배치 로딩 최적화
 */
export class BatchLoader<K, V> {
  private queue: Array<{ key: K; resolve: (value: V) => void; reject: (error: Error) => void }> = [];
  private timeoutId: NodeJS.Timeout | null = null;
  
  constructor(
    private batchFn: (keys: K[]) => Promise<V[]>,
    private options: {
      maxBatchSize?: number;
      batchInterval?: number;
    } = {}
  ) {
    this.options.maxBatchSize = options.maxBatchSize || 50;
    this.options.batchInterval = options.batchInterval || 10; // 10ms
  }
  
  /**
   * 개별 항목 로드 (배치로 처리됨)
   */
  load(key: K): Promise<V> {
    return new Promise((resolve, reject) => {
      this.queue.push({ key, resolve, reject });
      
      // 배치 크기 도달 시 즉시 처리
      if (this.queue.length >= this.options.maxBatchSize!) {
        this.processBatch();
        return;
      }
      
      // 타이머 설정 (배치 간격)
      if (!this.timeoutId) {
        this.timeoutId = setTimeout(() => {
          this.processBatch();
        }, this.options.batchInterval);
      }
    });
  }
  
  /**
   * 배치 처리 실행
   */
  private async processBatch(): void {
    const currentQueue = [...this.queue];
    this.queue = [];
    
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    
    if (currentQueue.length === 0) return;
    
    try {
      const keys = currentQueue.map(item => item.key);
      const results = await this.batchFn(keys);
      
      // 결과를 각 요청에 매핑
      currentQueue.forEach((item, index) => {
        if (results[index] !== undefined) {
          item.resolve(results[index]);
        } else {
          item.reject(new Error(`No result for key: ${item.key}`));
        }
      });
      
    } catch (error) {
      // 모든 요청에 에러 전파
      currentQueue.forEach(item => {
        item.reject(error instanceof Error ? error : new Error(String(error)));
      });
    }
  }
}

/**
 * 🔥 React 컴포넌트 성능 최적화 유틸리티
 */
export const performanceUtils = {
  
  /**
   * 무거운 계산을 위한 워커 스레드 활용
   */
  createWebWorker: (workerScript: string) => {
    if (typeof Worker === 'undefined') {
      console.warn('Web Workers not supported');
      return null;
    }
    
    const blob = new Blob([workerScript], { type: 'application/javascript' });
    return new Worker(URL.createObjectURL(blob));
  },
  
  /**
   * 이미지 지연 로딩 옵션
   */
  lazyImageOptions: {
    rootMargin: '100px',
    threshold: 0.1
  },
  
  /**
   * Firebase 쿼리 최적화 헬퍼
   */
  optimizedQuery: {
    // 페이지네이션을 위한 커서 기반 쿼리
    createCursorQuery: (lastDoc: any, limit: number = 20) => ({
      startAfter: lastDoc,
      limit
    }),
    
    // 복합 인덱스 활용 최적화
    createCompoundQuery: (filters: Record<string, any>) => {
      // 인덱스 순서에 맞게 필터 정렬
      const sortedFilters = Object.entries(filters)
        .sort(([a], [b]) => a.localeCompare(b));
      
      return sortedFilters.reduce((query, [field, value]) => {
        if (value !== undefined && value !== null) {
          return { ...query, [field]: value };
        }
        return query;
      }, {});
    }
  },
  
  /**
   * 메모리 사용량 모니터링
   */
  memoryMonitor: {
    getCurrentUsage: () => {
      if ('memory' in performance) {
        const memory = (performance as any).memory;
        return {
          used: Math.round(memory.usedJSHeapSize / 1024 / 1024),
          total: Math.round(memory.totalJSHeapSize / 1024 / 1024),
          limit: Math.round(memory.jsHeapSizeLimit / 1024 / 1024)
        };
      }
      return null;
    }
  },
  
  /**
   * 네트워크 상태 감지
   */
  networkOptimization: {
    isSlowConnection: () => {
      if ('connection' in navigator) {
        const connection = (navigator as any).connection;
        return connection.effectiveType === 'slow-2g' || 
               connection.effectiveType === '2g';
      }
      return false;
    },
    
    // 느린 연결에서 데이터 압축
    shouldCompress: () => {
      return performanceUtils.networkOptimization.isSlowConnection();
    }
  }
};

/**
 * 🔥 리소스 정리 유틸리티
 */
export class ResourceCleanup {
  private static timers = new Set<NodeJS.Timeout>();
  private static intervals = new Set<NodeJS.Timeout>();
  private static observers = new Set<IntersectionObserver | MutationObserver>();
  
  /**
   * 타이머 등록 및 관리
   */
  static setTimeout(callback: () => void, delay: number): NodeJS.Timeout {
    const timer = setTimeout(() => {
      callback();
      this.timers.delete(timer);
    }, delay);
    
    this.timers.add(timer);
    return timer;
  }
  
  /**
   * 인터벌 등록 및 관리
   */
  static setInterval(callback: () => void, delay: number): NodeJS.Timeout {
    const interval = setInterval(callback, delay);
    this.intervals.add(interval);
    return interval;
  }
  
  /**
   * 옵저버 등록 및 관리
   */
  static addObserver(observer: IntersectionObserver | MutationObserver): void {
    this.observers.add(observer);
  }
  
  /**
   * 모든 리소스 정리
   */
  static cleanupAll(): void {
    // 타이머 정리
    this.timers.forEach(timer => clearTimeout(timer));
    this.timers.clear();
    
    // 인터벌 정리
    this.intervals.forEach(interval => clearInterval(interval));
    this.intervals.clear();
    
    // 옵저버 정리
    this.observers.forEach(observer => observer.disconnect());
    this.observers.clear();
    
    console.log('✅ 모든 리소스 정리 완료');
  }
}

/**
 * 🔥 성능 측정 유틸리티
 */
export const performanceMeasure = {
  /**
   * 함수 실행 시간 측정
   */
  measureTime: async <T>(
    label: string, 
    fn: () => Promise<T> | T
  ): Promise<T> => {
    const start = performance.now();
    const result = await fn();
    const end = performance.now();
    
    console.log(`⚡ ${label}: ${(end - start).toFixed(2)}ms`);
    return result;
  },
  
  /**
   * React 렌더링 성능 측정
   */
  measureRender: (componentName: string) => {
    const start = performance.now();
    
    return () => {
      const end = performance.now();
      console.log(`🔄 ${componentName} 렌더링: ${(end - start).toFixed(2)}ms`);
    };
  }
};
